// src/components/ToastModal.tsx

import React from "react";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";

export type ToastType = "success" | "error" | "warning" | "info";

interface ToastModalProps {
    toastId: string;
    type: ToastType;
    title: string;
    subtitle?: string;
    detail?: string;
    buttonText?: string;
}

const config: Record<
    ToastType,
    {
        wrapperBg: string;
        iconBg: string;
        iconColor: string;
        subtitleColor: string;
        detailBorder: string;
        icon: React.ReactNode;
    }
> = {
    warning: {
        wrapperBg: "bg-amber-50",
        iconBg: "bg-amber-100",
        iconColor: "text-amber-500",
        subtitleColor: "text-amber-600",
        detailBorder: "border-amber-200 bg-amber-50/60",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
            </svg>
        ),
    },
    error: {
        wrapperBg: "bg-red-50",
        iconBg: "bg-red-100",
        iconColor: "text-red-500",
        subtitleColor: "text-red-600",
        detailBorder: "border-red-200 bg-red-50/60",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        ),
    },
    success: {
        wrapperBg: "bg-green-50",
        iconBg: "bg-green-100",
        iconColor: "text-green-500",
        subtitleColor: "text-green-600",
        detailBorder: "border-green-200 bg-green-50/60",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        ),
    },
    info: {
        wrapperBg: "bg-blue-50",
        iconBg: "bg-blue-100",
        iconColor: "text-blue-500",
        subtitleColor: "text-blue-600",
        detailBorder: "border-blue-200 bg-blue-50/60",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        ),
    },
};

export const ToastModal: React.FC<ToastModalProps> = ({
    toastId,
    type,
    title,
    subtitle,
    detail,
    buttonText = "Understood",
}) => {
    const c = config[type];

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div
                className={`${c.wrapperBg} rounded-2xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center text-center`}
            >
                {/* Icon */}
                <div
                    className={`${c.iconBg} ${c.iconColor} w-16 h-16 rounded-full flex items-center justify-center mb-5`}
                >
                    {c.icon}
                </div>

                {/* Title */}
                <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>

                {/* Subtitle */}
                {subtitle && <p className={`${c.subtitleColor} font-medium text-sm mb-4 leading-snug`}>{subtitle}</p>}

                {/* Detail box */}
                {detail && (
                    <div
                        className={`w-full border ${c.detailBorder} rounded-xl p-4 mb-6 text-sm text-gray-700 flex items-start gap-2 text-left leading-relaxed`}
                    >
                        <span className="text-base mt-0.5">💡</span>
                        <span>{detail}</span>
                    </div>
                )}

                {/* Button */}
                <button
                    onClick={() => toast.dismiss(toastId)}
                    className="bg-gray-900 hover:bg-gray-700 text-white w-full py-3 rounded-xl font-semibold text-sm transition"
                >
                    {buttonText}
                </button>
            </div>
        </div>,
        document.body,
    );
};
